/**
 * Pure distance calculation functions
 * These functions have no dependencies on services or platform-specific code
 * Can be easily ported to other platforms
 */

import { Coordinate, CoordinateWithId } from "../types/distance";

// Constants
const EARTH_RADIUS_METERS = 6371e3;

/**
 * Calculate aerial distance between two points using Haversine formula
 * @param point1 - First coordinate
 * @param point2 - Second coordinate
 * @returns Distance in meters
 */
export const calculateAerialDistance = (
  point1: Coordinate,
  point2: Coordinate
): number => {
  const φ1 = (point1.latitude * Math.PI) / 180;
  const φ2 = (point2.latitude * Math.PI) / 180;
  const Δφ = ((point2.latitude - point1.latitude) * Math.PI) / 180;
  const Δλ = ((point2.longitude - point1.longitude) * Math.PI) / 180;

  const a =
    Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
    Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_METERS * c;
};

/**
 * Get the N closest destinations based on aerial distance (for pre-filtering)
 * This is a quick calculation to reduce API calls
 * @param origin - Origin coordinates
 * @param destinations - Array of destination coordinates with IDs
 * @param limit - Number of closest destinations to return (default: 5)
 * @returns Top N closest destinations with aerial distance
 */
export const getClosestDestinationsByAerialDistance = (
  origin: Coordinate,
  destinations: CoordinateWithId[],
  limit: number = 5
): Array<CoordinateWithId & { aerialDistance: number }> => {
  const destinationsWithDistance = destinations.map((dest) => {
    const aerialDistance = calculateAerialDistance(origin, dest);
    return { ...dest, aerialDistance };
  });

  // Sort by aerial distance and return top N
  return destinationsWithDistance
    .sort((a, b) => a.aerialDistance - b.aerialDistance)
    .slice(0, limit);
};

